import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Shield, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AccessCodePage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const verifyCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      toast.error("Please enter your access code");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/verify-access-code`, { access_code: code.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Access code verified! Welcome to the admin panel.");
      navigate("/admin", { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid access code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md rounded-3xl shadow-xl border-0" data-testid="access-code-card">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <KeyRound className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold">Access Code Required</CardTitle>
          <p className="text-sm text-gray-500 mt-2">
            Enter the access code provided by your administrator to continue to the admin panel.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={verifyCode} className="space-y-4">
            <div className="space-y-2">
              <Label>Access Code</Label>
              <Input
                type="text"
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase())}
                placeholder="Enter your access code"
                className="rounded-xl text-center font-mono text-lg tracking-widest"
                maxLength={16}
                autoFocus
                data-testid="access-code-input"
              />
            </div>
            <Button type="submit" className="w-full rounded-xl" disabled={loading} data-testid="verify-code-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
              Verify & Continue
            </Button>
            <Button type="button" variant="outline" className="w-full rounded-xl" onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn">
              Sign Out
            </Button>
          </form>
          {user && (
            <p className="text-xs text-center text-gray-400 mt-4">
              Signed in as {user.email}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
